import { useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api, errMsg } from "@/lib/api";

const BLANK = { name: "", contact: "", email: "", phone: "", notes: "" };

export function VendorsPanel({ vendors, reload }) {
  const [f, setF] = useState(BLANK);
  const [editId, setEditId] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editId) await api.put(`/admin/vendors/${editId}`, f);
      else await api.post("/admin/vendors", f);
      toast.success(editId ? "Vendor updated" : "Vendor added");
      setF(BLANK);
      setEditId(null);
      reload();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/admin/vendors/${deleting.id}`);
      toast.success("Vendor deleted");
      reload();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="space-y-6">
      <form onSubmit={save} className="grid grid-cols-1 items-end gap-4 rounded-lg border border-border bg-card p-6 sm:grid-cols-2 lg:grid-cols-[1fr_1fr_1fr_1fr_auto]">
        <div className="space-y-1.5"><Label>Vendor name</Label><Input required value={f.name} onChange={set("name")} data-testid="vendor-name-input" /></div>
        <div className="space-y-1.5"><Label>Contact person</Label><Input value={f.contact} onChange={set("contact")} data-testid="vendor-contact-input" /></div>
        <div className="space-y-1.5"><Label>Email</Label><Input value={f.email} onChange={set("email")} data-testid="vendor-email-input" /></div>
        <div className="space-y-1.5"><Label>Phone / WhatsApp</Label><Input value={f.phone} onChange={set("phone")} data-testid="vendor-phone-input" /></div>
        <div className="flex gap-2">
          <Button type="submit" className="gap-2" data-testid="vendor-save-button"><Plus className="h-4 w-4" />{editId ? "Save" : "Add vendor"}</Button>
          {editId && <Button type="button" variant="ghost" onClick={() => { setEditId(null); setF(BLANK); }} data-testid="vendor-cancel-edit-button">Cancel</Button>}
        </div>
      </form>
      <div className="divide-y divide-border rounded-lg border border-border bg-card">
        {vendors.map((v) => (
          <div key={v.id} className="flex items-center justify-between gap-4 p-4" data-testid={`vendor-row-${v.id}`}>
            <div>
              <p className="font-medium">{v.name}</p>
              <p className="font-mono-data text-xs text-muted-foreground">{[v.contact, v.email, v.phone].filter(Boolean).join(" · ") || "No contact details"}</p>
            </div>
            <div className="flex gap-1">
              <Button size="icon" variant="ghost" onClick={() => { setEditId(v.id); setF({ ...BLANK, ...v }); }} data-testid={`vendor-edit-${v.id}`}><Pencil className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" onClick={() => setDeleting(v)} data-testid={`vendor-delete-${v.id}`}><Trash2 className="h-4 w-4 text-red-400" /></Button>
            </div>
          </div>
        ))}
        {vendors.length === 0 && <p className="py-10 text-center text-sm text-muted-foreground" data-testid="vendors-empty">No vendors yet. Add one above.</p>}
      </div>
      <AlertDialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleting?.name}?</AlertDialogTitle>
            <AlertDialogDescription>Past orders from this vendor stay in your history.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="vendor-delete-cancel">Keep</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="vendor-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
